import styled from 'styled-components';
import Card from 'components/Card';

// Full Page Container
export const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;
  min-height: 100vh;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

export const PageTitle = styled.h2`
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  font-size: 28px;
  margin-bottom: 12px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    margin-bottom: 8px;
  }
`;

// Page Content Area
export const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Stats Bar
export const StatsBar = styled.div`
  display: flex;
  gap: 24px;
  padding: 20px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(250,70,22,0.05)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(250,70,22,0.15)'};
  border-radius: 12px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    gap: 12px;
  }
`;

export const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#6B7280')};

  span {
    display: flex;
    align-items: center;
    gap: 4px;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.08em;
    font-weight: 600;
  }
`;

export const StatIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
`;

export const StatValue = styled.span`
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  font-size: 16px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
`;

// Content Grid for two column layout
export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Container for Target Audience and Credits Required
export const AudienceCreditsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

// Channel Section
export const ChannelSection = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 12px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

export const TargetAudienceSection = styled(ChannelSection)`
  flex: 0 0 35%;

  @media (max-width: 768px) {
    flex: 1;
  }
`;

export const CreditsRequiredSection = styled(ChannelSection)`
  flex: 1;
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;

export const ChannelTabs = styled.div`
  display: flex;
  gap: 8px;
`;

export const ChannelTab = styled.button`
  padding: 10px 20px;
  border: 1px solid
    ${(props) =>
      props.active
        ? props.theme?.colors?.primary || '#FA4616'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : '#E5E7EB'};
  background: ${(props) =>
    props.active ? 'rgba(250,70,22,0.1)' : 'transparent'};
  color: ${(props) =>
    props.active
      ? props.theme?.colors?.primary || '#FA4616'
      : props.theme.name === 'dark'
      ? '#E5E7EB'
      : '#6B7280'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;

  &:hover {
    background: ${(props) =>
      props.active ? 'rgba(250,70,22,0.15)' : 'rgba(250,70,22,0.05)'};
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }
`;

// Compose Section
export const ComposeSection = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 12px;
  padding: 20px;
`;

export const TitleInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(255,255,255,1)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.15)'};
  border-radius: 8px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#334155')};
  font-size: 14px;
  font-family: inherit;
  transition: all 0.15s ease;
  box-sizing: border-box;
  margin-bottom: 8px;

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#64748B')};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(255,255,255,1)'};
    box-shadow: ${(props) =>
      props.theme.name === 'dark'
        ? `0 0 0 1px ${props.theme?.colors?.primary || '#FA4616'}`
        : '0 0 0 3px rgba(250,70,22,0.1)'};
  }
`;

export const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgba(255,255,255,1)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.15)'};
  border-radius: 8px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#334155')};
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.15s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#64748B')};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(255,255,255,1)'};
    box-shadow: ${(props) =>
      props.theme.name === 'dark'
        ? `0 0 0 1px ${props.theme?.colors?.primary || '#FA4616'}`
        : '0 0 0 3px rgba(250,70,22,0.1)'};
  }
`;

export const CharacterCount = styled.div`
  text-align: right;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#6B7280')};
  margin-top: 8px;
`;

export const QuickVariables = styled.div`
  margin-top: 16px;
`;

export const VariableButton = styled.button`
  padding: 8px 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#F9FAFB'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E5E7EB'};
  border-radius: 8px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#6B7280')};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(250, 70, 22, 0.1);
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }
`;

// Preview Section - Now using shared Card component
export const PreviewSection = Card;

// Pro Tips
export const ProTips = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(16,185,129,0.1)'
      : 'rgba(16,185,129,0.05)'};
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  padding: 20px;
`;

export const TipItem = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme?.colors?.success || '#22C55E'
      : props.theme?.colors?.success || '#16A34A'};
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Footer Buttons
export const FooterButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const NextButton = styled.button`
  padding: 12px 24px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.05)'};
  border: 2px solid ${(props) => props.theme?.colors?.primary || '#fa4616'};
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;

  @media (max-width: 480px) {
    width: 100%;
    margin-left: 0;
  }

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.2)'
        : 'rgba(250, 70, 22, 0.1)'};
    transform: translateY(-1px);
  }
`;

export const SendButton = styled(NextButton)`
  // Inherit the transparent background and border from NextButton
  // No need to override since NextButton already has the right styling
`;

// Review Section Styles
export const ReviewSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

export const ReviewLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

export const ReviewRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const DetailCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 12px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

export const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#F1F5F9'};

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const DetailLabel = styled.span`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#6B7280')};
  display: flex;
  align-items: center;
`;

export const DetailValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
`;

export const ScheduleToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
`;

export const ToggleLabel = styled.label`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#4B5563')};
`;

export const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #cbd5e1;
    transition: 0.4s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: '';
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.4s;
      border-radius: 50%;
    }
  }

  input:checked + .slider {
    background-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }

  input:checked + .slider:before {
    transform: translateX(24px);
  }
`;

export const ExpectedResults = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(59,130,246,0.1)'
      : 'rgba(59,130,246,0.05)'};
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 20px;
`;

export const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

export const ResultLabel = styled.span`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#6B7280')};
`;

export const ResultValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#3B82F6' : '#2563EB')};
`;

export const FinalPreview = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.08)'};
  border-radius: 12px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`;

export const AudienceInfo = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250,70,22,0.1)'
      : 'rgba(250,70,22,0.05)'};
  border: 1px solid rgba(250, 70, 22, 0.2);
  border-radius: 8px;
  padding: 16px;
`;

export const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;

  &:first-child {
    color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#6B7280')};
  }

  span:last-child {
    font-weight: 600;
    color: ${(props) =>
      props.theme.name === 'dark' ? '#ffffff' : props.theme.colors.blueBlack};
  }
`;

export const ReadyStatus = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(16,185,129,0.1)'
      : 'rgba(16,185,129,0.05)'};
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  margin-top: 20px;

  @media (max-width: 768px) {
    padding: 16px;
    gap: 12px;
    margin-top: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
    margin-top: 12px;
  }
`;

export const StatusIcon = styled.div`
  width: 24px;
  height: 24px;
  background: ${(props) => props.theme?.colors?.success || '#22C55E'};
  color: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
`;

export const StatusText = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme?.colors?.success || '#22C55E'
      : props.theme?.colors?.success || '#16A34A'};
  margin: 0 0 4px 0;
`;

export const ROIHighlight = styled.div`
  padding: 16px;
  background: linear-gradient(
    135deg,
    rgba(250, 70, 22, 0.1) 0%,
    rgba(249, 115, 22, 0.05) 100%
  );
  border: 1px solid rgba(250, 70, 22, 0.3);
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#FCA071' : '#DC2626')};
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  strong {
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    font-weight: 700;
  }
`;

// Deprecated modal styles (kept for backwards compatibility but hidden)
export const CloseButton = styled.button`
  display: none;
`;

export const ModalOverlay = styled.div`
  display: none;
`;

export const ModalContainer = styled.div`
  display: none;
`;

export const ModalHeader = styled.div`
  display: none;
`;

export const ModalBody = styled.div`
  display: none;
`;

// Attachment Upload Components
export const AttachmentSection = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#FAFBFC'};
  border: 1px dashed
    ${(props) =>
      props.hasFiles
        ? props.theme.name === 'dark'
          ? 'rgba(250,70,22,0.3)'
          : 'rgba(250,70,22,0.4)'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : '#D1D5DB'};
  border-radius: 8px;
  margin-top: 12px;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

export const AttachmentHeader = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
`;

export const AddImageButton = styled.button`
  padding: 6px 14px;
  background: ${(props) =>
    props.disabled ? '#9CA3AF' : props.theme?.colors?.primary || '#FA4616'};
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: #e03e0c;
    transform: translateY(-1px);
  }
`;

export const AttachmentInfo = styled.span`
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  flex: 1;
`;

export const AttachmentStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const FileCountBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250,70,22,0.15)'
      : 'rgba(250,70,22,0.1)'};
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

export const CreditImpactBadge = styled.span`
  padding: 4px 10px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(251,191,36,0.15)'
      : 'rgba(251,191,36,0.1)'};
  color: ${(props) => (props.theme.name === 'dark' ? '#FCD34D' : '#F59E0B')};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

export const ExpandIcon = styled.div`
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  transition: transform 0.2s ease;
  transform: ${(props) => (props.expanded ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const AttachmentContent = styled.div`
  padding: 0 12px 12px 12px;
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  max-width: 350px;
`;

export const FileItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.1)'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover .file-overlay {
    opacity: 1;
  }
`;

export const FileOverlay = styled.div.attrs({ className: 'file-overlay' })`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

export const FileSize = styled.span`
  font-size: 11px;
  color: #ffffff;
  font-weight: 500;
`;

export const RemoveFileButton = styled.button`
  align-self: flex-end;
  width: 24px;
  height: 24px;
  background: rgba(239, 68, 68, 0.9);
  color: #ffffff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #dc2626;
    transform: scale(1.1);
  }
`;

export const AddMoreArea = styled.div`
  aspect-ratio: 1;
  border: 2px dashed
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(0,0,0,0.02)'};
  color: ${(props) => (props.theme.name === 'dark' ? '#6B7280' : '#9CA3AF')};

  &:hover {
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    background: rgba(250, 70, 22, 0.05);
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }
`;

export const MMSAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(251,191,36,0.1)'
      : 'rgba(251,191,36,0.05)'};
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  margin-top: 12px;
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#FCD34D' : '#F59E0B')};

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const DrawerSection = styled.div`
  margin-bottom: 24px;

  p {
    line-height: 1.6;
    margin: 8px 0;
  }
`;

export const DrawerSubtitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  margin-bottom: 12px;
`;

export const VariableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const VariableItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.02)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
`;

export const VariableName = styled.code`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.05)'};
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 13px;
  white-space: nowrap;
  min-width: 140px;
`;

export const VariableDescription = styled.span`
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  font-size: 14px;
  line-height: 1.5;
`;

export const ExampleBox = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.02)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 8px;
  padding: 16px;
`;

export const ExampleLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  margin-bottom: 8px;
  font-weight: 500;
`;
